import React, {useCallback, useEffect, useRef, useState} from 'react';
import {usePanorama} from '@wix/yoshi-flow-editor';
import {FormViewer} from '@wix/form-viewer/widget';
import {Accordion, AccordionTheme, TextButton, Divider, TextButtonPriority, Spinner} from '@wix/wix-ui-tpa/cssVars';
import {FormModel} from '../../../../../../domain/models/checkout/Cart/Form.model';
import {classes} from './AdditionalDetailsStepOpen.st.css';
import {useICUI18n} from '../../../../Hooks/useICUI18n';
import {useFormInstance} from '../../../../Form/useFormInstance';
import {useAdditionalDetailsData} from './WithAdditionalDetailsData';
import {useLocaleKeys} from '../../../../../../locale-keys/LocaleKeys';
import {useControllerProps} from '../../../../Widget/ControllerContext';

export enum AdditionalDetailsFormDataHook {
  formItem = 'AdditionalDetailsForm.formItem',
  formItemAccordion = 'AdditionalDetailsForm.formItemAccordion',
  formItemContent = 'AdditionalDetailsForm.formItemContent',
  nextButton = 'AdditionalDetailsForm.nextButton',
  nextButtonLoader = 'AdditionalDetailsForm.nextButtonLoader',
}

interface AdditionalDetailsFormProps {
  form: FormModel;
  isInitiallyExpanded: boolean;
  shouldShowNextButton: boolean;
}

export const AdditionalDetailsForm: React.FC<AdditionalDetailsFormProps> = ({
  form,
  isInitiallyExpanded,
  shouldShowNextButton,
}) => {
  const {id, formSchemaId, title, description} = form;
  const i18n = useICUI18n();
  const panorama = usePanorama();
  const [isCurrentFormSubmitting, setIsCurrentFormSubmitting] = useState(false);

  const {
    formsStore: {areFormsSubmitting},
  } = useControllerProps();

  const localeKeys = useLocaleKeys();
  const {data: formInstance} = useFormInstance({});
  const {registerForm, unregisterForm, isFormValid, submitAndContinueToNextForm} = useAdditionalDetailsData();

  const {formValues, formErrors, formRef, setFormValues, setFormErrors} = formInstance;
  const accordionRef = useRef<Accordion>(null);

  const focusOnForm = useCallback(
    () => {
      accordionRef.current?.expandItem(id);

      setTimeout(() => {
        accordionRef.current?.rootRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 300);
    },
    /* eslint-disable react-hooks/exhaustive-deps */ []
  );

  const handleNextButtonClick = async () => {
    setIsCurrentFormSubmitting(true);
    const isValid = await isFormValid(id);
    if (!isValid) {
      return;
    }

    setIsCurrentFormSubmitting(true);
    submitAndContinueToNextForm({currentForId: id});
    accordionRef.current?.collapseItem(id);
  };

  useEffect(
    () => {
      registerForm(id, {
        focusOnForm,
        formInstance,
      });

      return () => {
        unregisterForm(id);
      };
    },
    /* eslint-disable react-hooks/exhaustive-deps*/ [id, formRef]
  );

  useEffect(() => {
    if (!areFormsSubmitting) {
      setIsCurrentFormSubmitting(false);
    }
  }, [areFormsSubmitting]);

  return (
    <li className={classes.eventItem} data-hook={AdditionalDetailsFormDataHook.formItem}>
      <Accordion
        ref={accordionRef}
        data-hook={AdditionalDetailsFormDataHook.formItemAccordion}
        theme={AccordionTheme.Box}
        initiallyExpanded={isInitiallyExpanded ? [id] : undefined}>
        <Accordion.Item id={id} key={id} title={title} subtitle={description?.join(' | ')}>
          <div
            role="presentation"
            onKeyDown={(e) => e.stopPropagation()}
            className={classes.formItemContent}
            data-hook={AdditionalDetailsFormDataHook.formItemContent}>
            <Divider className={classes.divider} />
            <FormViewer
              i18n={i18n}
              ref={formRef}
              panorama={panorama}
              formId={formSchemaId}
              values={formValues}
              errors={formErrors}
              onChange={setFormValues}
              onValidate={setFormErrors}
            />

            {shouldShowNextButton && (
              <TextButton
                disabled={areFormsSubmitting}
                data-hook={AdditionalDetailsFormDataHook.nextButton}
                priority={TextButtonPriority.link}
                className={classes.nextButton}
                contentClassName={classes.nextButtonContent}
                onClick={() => void handleNextButtonClick()}>
                {!isCurrentFormSubmitting ? (
                  localeKeys.checkout.additionalDetailsStep.next.cta()
                ) : (
                  <Spinner
                    diameter={18}
                    data-hook={AdditionalDetailsFormDataHook.nextButtonLoader}
                    className={classes.nextButtonLoader}
                  />
                )}
              </TextButton>
            )}
          </div>
        </Accordion.Item>
      </Accordion>
    </li>
  );
};
